import { useEffect, useState, type FormEvent } from "react";
import { api } from "../lib/api";
import { Aviso, Campo } from "../componentes/piezas";
import { SelectorIdioma, useIdioma, type Idioma } from "../lib/idioma";

/**
 * Solicitud de alta desde fuera: la rellena alguien que todavía no es socio y
 * el club la aprueba o la rechaza. No crea nada por si sola.
 */
export default function PortalAlta({ club }: { club: string }) {
  const { t, idioma, loEligio, aplicarPorDefecto, delFallo } = useIdioma();
  const [nombreClub, setNombreClub] = useState<string | null>(null);
  const [enviada, setEnviada] = useState(false);
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  const [datos, setDatos] = useState({
    nombre: "",
    apellidos: "",
    documentoTipo: "DNI",
    documentoNumero: "",
    fechaNacimiento: "",
    telefono: "",
    email: "",
    ciudad: "",
    avalistaNumero: "",
    mensaje: "",
    consentimiento: false,
  });

  useEffect(() => {
    void api<{ nombre: string; idioma?: Idioma }>(
      `/portal/club/${encodeURIComponent(club)}`,
    )
      .then((c) => {
        setNombreClub(c.nombre);
        aplicarPorDefecto(c.idioma);
      })
      .catch(() => setError(t("alta.clubNoEncontrado")));
    // El texto del error se fija al cargar; cambiar de idioma despues no lo reescribe, y no importa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [club]);

  const cambiar = (campo: string, valor: string | boolean) =>
    setDatos((d) => ({ ...d, [campo]: valor }));

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError("");
    setEnviando(true);
    try {
      await api("/portal/solicitud", {
        metodo: "POST",
        cuerpo: {
          ...datos,
          club,
          /*
           * En el que esta rellenando esto, si lo ha elegido: es lo unico que
           * sabemos de el todavia y de ahi sale el correo de respuesta.
           *
           * Si no lo eligio, va vacio a proposito. Poner aqui el del club lo
           * dejaria escrito en su ficha para siempre, y entonces el dia que el
           * club cambia de idioma en Ajustes no cambia nada para ninguno de los
           * que entraron por el portal.
           */
          idioma: loEligio ? idioma : null,
          telefono: datos.telefono || null,
          email: datos.email || null,
          ciudad: datos.ciudad || null,
          avalistaNumero: datos.avalistaNumero
            ? Number(datos.avalistaNumero)
            : null,
          mensaje: datos.mensaje || null,
        },
      });
      setEnviada(true);
    } catch (fallo) {
      setError(delFallo(fallo, "alta.fallo"));
    } finally {
      setEnviando(false);
    }
  }

  if (enviada) {
    return (
      <div className="portal-entrada">
        <div className="portal-caja centrado">
          <h1 style={{ fontSize: "1.2rem" }}>{t("alta.enviada")}</h1>
          <p className="suave">
            {t("alta.enviadaTexto", { club: nombreClub ?? t("alta.elClub") })}
          </p>
          <a className="boton bloque" href={`/portal/${club}`}>
            {t("general.volver")}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-entrada">
      <form className="portal-caja" onSubmit={enviar}>
        <div className="centrado" style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: "1.2rem" }}>{t("alta.titulo")}</h1>
          <p className="suave pequeno" style={{ margin: "4px 0 0" }}>
            {nombreClub ?? t("general.cargando")}
          </p>
        </div>

        <Aviso tipo="error">{error}</Aviso>

        <div className="linea-campos">
          <Campo etiqueta={t("alta.nombre")}>
            <input
              value={datos.nombre}
              onChange={(e) => cambiar("nombre", e.target.value)}
              required
              autoFocus
            />
          </Campo>
          <Campo etiqueta={t("alta.apellidos")}>
            <input
              value={datos.apellidos}
              onChange={(e) => cambiar("apellidos", e.target.value)}
              required
            />
          </Campo>
        </div>

        <div className="linea-campos">
          <Campo etiqueta={t("alta.documento")}>
            <select
              value={datos.documentoTipo}
              onChange={(e) => cambiar("documentoTipo", e.target.value)}
            >
              <option value="DNI">DNI</option>
              <option value="NIE">NIE</option>
              <option value="PASAPORTE">{t("alta.pasaporte")}</option>
            </select>
          </Campo>
          <Campo etiqueta={t("alta.numero")}>
            <input
              value={datos.documentoNumero}
              onChange={(e) =>
                cambiar("documentoNumero", e.target.value.toUpperCase())
              }
              required
            />
          </Campo>
        </div>

        <Campo
          etiqueta={t("alta.fechaNacimiento")}
          ayuda={t("alta.mayorDeEdad")}
        >
          <input
            type="date"
            value={datos.fechaNacimiento}
            onChange={(e) => cambiar("fechaNacimiento", e.target.value)}
            required
          />
        </Campo>

        <div className="linea-campos">
          <Campo etiqueta={t("alta.telefono")}>
            <input
              value={datos.telefono}
              onChange={(e) => cambiar("telefono", e.target.value)}
            />
          </Campo>
          <Campo etiqueta={t("alta.email")}>
            <input
              type="email"
              value={datos.email}
              onChange={(e) => cambiar("email", e.target.value)}
            />
          </Campo>
        </div>

        <div className="linea-campos">
          <Campo etiqueta={t("alta.ciudad")}>
            <input
              value={datos.ciudad}
              onChange={(e) => cambiar("ciudad", e.target.value)}
            />
          </Campo>
          <Campo etiqueta={t("alta.avalista")} ayuda={t("alta.avalistaAyuda")}>
            <input
              type="number"
              min={1}
              value={datos.avalistaNumero}
              onChange={(e) => cambiar("avalistaNumero", e.target.value)}
            />
          </Campo>
        </div>

        <Campo etiqueta={t("alta.mensaje")}>
          <textarea
            rows={3}
            value={datos.mensaje}
            onChange={(e) => cambiar("mensaje", e.target.value)}
          />
        </Campo>

        <label className="casilla">
          <input
            type="checkbox"
            checked={datos.consentimiento}
            onChange={(e) => cambiar("consentimiento", e.target.checked)}
            required
          />
          <span>
            {t("alta.consentimiento")}
            <br />
            <span className="ayuda">{t("alta.consentimientoAyuda")}</span>
          </span>
        </label>

        <button
          className="boton grande bloque"
          disabled={enviando || !datos.consentimiento}
        >
          {enviando ? t("general.enviando") : t("alta.enviar")}
        </button>

        <p className="centrado pequeno suave" style={{ marginBottom: 0 }}>
          <a href={`/portal/${club}`}>{t("alta.yaSoySocio")}</a>
        </p>

        <div className="centrado" style={{ marginTop: 14 }}>
          <SelectorIdioma />
        </div>
      </form>
    </div>
  );
}
